// Native & external imports
import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  OnDestroy,
  Output,
  Renderer2,
  inject,
} from "@angular/core";
import { Subject } from "rxjs";
import { debounceTime } from "rxjs/operators";
import { SubSink } from "subsink";

// Objects
import { CmpFauxSelectOptionObject } from "src/objects/cmp-faux-dropdown-with-inline-ui.object";
import {
  CmpFauxDropdownOptionsObject,
  CmpSchoolMiniObject,
} from "src/objects/cmp-staff.object";
import { LoadingService } from "src/services/loading.service";

@Component({
  standalone: false,
  selector: "cmp-faux-dropdown-with-inline-ui",
  templateUrl: "./faux-dropdown-with-inline-ui.component.html",
})
export class FauxDropdownWithInlineUiComponent implements OnInit, OnDestroy {
  private renderer = inject(Renderer2);

  @Input() options: CmpFauxDropdownOptionsObject =
    new CmpFauxDropdownOptionsObject();
  @Input() isInstitute = false;
  @Input() set clearInputValue(value: boolean) {
    if (value) {
      this.clearInput();
    }
  }
  @Output() selectedValue = new EventEmitter();
  public chosenOption: CmpFauxSelectOptionObject =
    new CmpFauxSelectOptionObject();
  public searchContent = "";
  public requiredCount = 0;
  public showMenu = false;
  public disabled = false;
  public focus = false;
  public filteredOptions: Array<CmpSchoolMiniObject> = [];
  public uniqId = `id${new Date().getTime()}`; // For multiple dropdown used timestamp for selecting current dropdown
  public searchUpdate = new Subject<string>();
  private subSink = new SubSink();
  public loading: LoadingService<["search"]>;
  constructor() {
    this.loading = new LoadingService();
  }

  ngOnInit(): void {
    // restricting consequent call while search
    this.requiredCount = this.isInstitute ? 2 : 3;
    this.subSink.add(
      this.searchUpdate.pipe(debounceTime(300)).subscribe((value) => {
        this.search();
      }),
    );
  }

  ngOnDestroy() {
    this.subSink.unsubscribe();
  }

  /**
   * @method hideMenu
   * @description Hides the drop-down options
   */
  public hideMenu() {
    this.showMenu = false;
  }

  /**
   * @method onEnterKeyPress
   * @description it will show dropdown options event if enter key pressed
   */
  public onEnterKeyPress(event: KeyboardEvent) {
    this.showMenu = true;
    if (event.code === "Enter" || event.code === "NumpadEnter") {
      event.preventDefault();
    }
  }

  /**
   * @method setSelectOption
   * @description Emits the selected option to the parent component
   */
  public setSelectOption(selectOption: CmpFauxSelectOptionObject) {
    this.searchContent = selectOption.name;
    this.showMenu = false;
    this.disabled = true;
    this.selectedValue.emit({
      from: "option",
      value: selectOption,
      content: this.searchContent,
    });
  }

  /**
   * @method customInput
   * @description Emits the selected option to the parent component
   */
  public customInput() {
    this.showMenu = false;
    this.selectedValue.emit({
      from: "custom",
      value: "",
      content: this.searchContent,
    });
  }

  /**
   * @method search
   * @description search option by name, state, city, abbreviation
   */
  private search() {
    this.filteredOptions = [];
    if (this.searchContent.length < this.requiredCount) {
      this.showMenu = false;
      this.selectedValue.emit({
        from: "option",
        value: null,
        content: this.searchContent,
      });
    }

    if (this.searchContent) {
      this.loading.start("search");
      if (this.searchContent.length >= this.requiredCount) {
        this.showMenu = true;
        const searchString = this.searchContent.toLocaleLowerCase();
        const splittedString = searchString.split(" ");
        this.filteredOptions = this.options.schools.filter(
          (item: CmpSchoolMiniObject) => {
            if (splittedString.length === 1) {
              this.loading.done("search");
              return (
                item.name?.toLowerCase().includes(searchString) ||
                item.miniText?.toLowerCase().includes(searchString) ||
                item.miniText2?.toLowerCase().includes(searchString) ||
                item.abbreviation?.toLowerCase().includes(searchString)
              );
            }
            this.loading.done("search");
            return (
              splittedString.every((e) =>
                item.name?.toLowerCase().includes(e),
              ) ||
              splittedString.every((e) =>
                item.miniText?.toLowerCase().includes(e),
              ) ||
              splittedString.every((e) =>
                item.miniText2?.toLowerCase().includes(e),
              ) ||
              splittedString.every((e) =>
                item.abbreviation?.toLowerCase().includes(e),
              )
            );
          },
        );

        // const size = (this.filteredOptions.length * 3.1) - 45;
        // this.size = (size <= 0) ? `${size}rem` : `-${size}rem`;
        setTimeout(() => {
          let size: string | number = 0;
          if (
            this.filteredOptions.length > 0 &&
            this.filteredOptions.length < 10
          ) {
            const element = this.renderer.selectRootElement(
              "#inline-ui-content-1",
              true,
            );
            const height =
              element.offsetHeight / this.filteredOptions.length / 16;
            size = this.filteredOptions.length * height - 36;
          } else if (this.filteredOptions.length === 0) {
            size = 3 * 3.7 - 35;
          } else if (this.filteredOptions.length > 10) {
            size = -13;
          }
          size = size <= 0 ? `${size}rem` : `-${size}rem`;
          this.selectedValue.emit({
            from: "option",
            value: null,
            content: this.searchContent,
            size: size,
            showDropdown:
              this.filteredOptions.length > 0 &&
              this.searchContent.length >= this.requiredCount &&
              this.showMenu,
          });
        }, 200);
      }
    }
  }

  /**
   * @method addRemoveClass
   * @description add or remove the input-focused class
   * @param action string
   * @returns void
   */
  public addRemoveClass(action = "") {
    this.focus =
      action === "focus" ? true : this.searchContent.length > 0 ? true : false;
  }

  /**
   * @method clearInput
   * @description it will used to clear all the input fields
   * @returns void
   */
  public clearInput() {
    this.searchContent = "";
    this.showMenu = false;
    this.disabled = false;
    this.focus = false;
    const element: HTMLInputElement =
      this.renderer.selectRootElement("#search");
    setTimeout(() => {
      element.focus();
    });
    this.selectedValue.emit({
      from: "option",
      value: null,
      content: this.searchContent,
    });
  }

  /**
   * @method onClick
   * @description Hides the drop-down option when the user clicks outside the drop-down
   */
  // @HostListener('document:click', ['$event.target'])
  // public onClick(target: HTMLElement) {
  // const element = this.renderer.selectRootElement(`#${this.uniqId}`, true);
  // const clickedInside = element.contains(target);
  // if (!clickedInside && ((this.filteredOptions.length === 0 && this.searchContent !== '')) {
  //   this.showMenu = false;
  //   this.selectedValue.emit({
  //     from: 'option'
  //   });
  // }
  // }
}
